import Case from 'case';
import omit from 'lodash.omit';
import type { Contract, ValueProcessor } from 'validata';
import {
  asArray,
  isObject,
  isString,
  maybeArray,
  maybeBoolean,
  maybeString,
  StringFormat,
} from 'validata';
import { isObjectSet } from 'validata-mongo';
import { Person } from './person.js';
import type { RequestContext } from './request-context.js';

// Helper function for optional email fields that treats empty strings as undefined
export const maybeEmailString = (options?: {
  allowDisplayName?: boolean;
}): ValueProcessor<string | undefined> => {
  return {
    process: (input) => {
      // If input is an empty string, treat it as undefined
      if (input === '') {
        return { tag: 'value', value: undefined };
      }

      // Otherwise, use the standard maybeString validation with email format
      return maybeString({ format: StringFormat.email(options) }).process(
        input,
      );
    },
  };
};

export namespace Organization {
  export interface Type {
    _id: string;
    allowQuickPatrol?: boolean;
    callSigns?: string[];
    code: string;
    created: RequestContext;
    eccReplyCcEmail?: string;
    emailFrom?: string;
    emailSignOff?: string;
    hideUnstartedPatrolsBadge?: boolean;
    name: string;
    patrollerInstructions?: string;
    patrolMeetUpInstructions?: string;
    patrolNames?: string[];
    policeStation: string;
    restrictCallSigns?: boolean;
    updated: RequestContext;
  }

  export const DEFAULTS = {
    emailFrom: 'Patrol Roster <roster@patrol.org.nz>',
    emailSignOff: 'Patrol Roster team',
    patrollerInstructions: '',
    patrolMeetUpInstructions: '',
  } as const;

  export type Admin = Omit<Person.Create, 'organizationId' | 'roles'>;

  export type Create = Pick<
    Type,
    | 'code'
    | 'name'
    | 'policeStation'
    | 'emailFrom'
    | 'emailSignOff'
    | 'patrollerInstructions'
    | 'patrolMeetUpInstructions'
    | 'patrolNames'
    | 'eccReplyCcEmail'
    | 'allowQuickPatrol'
    | 'callSigns'
    | 'restrictCallSigns'
    | 'hideUnstartedPatrolsBadge'
  > & { admin: Admin };
  export type Patch = Pick<
    Type,
    | 'name'
    | 'policeStation'
    | 'emailFrom'
    | 'emailSignOff'
    | 'patrollerInstructions'
    | 'patrolMeetUpInstructions'
    | 'patrolNames'
    | 'eccReplyCcEmail'
    | 'allowQuickPatrol'
    | 'callSigns'
    | 'restrictCallSigns'
    | 'hideUnstartedPatrolsBadge'
  >;

  export const createContract: Contract<Create> = {
    admin: isObject<Admin>(
      omit(Person.createContract, 'organizationId', 'roles'),
    ),
    allowQuickPatrol: maybeBoolean(),
    callSigns: maybeArray(
      isString({
        minLength: 2,
        transform: (value) => value.toLocaleUpperCase(),
        trim: 'both',
      }),
    ),
    code: isString({
      maxLength: 5,
      minLength: 4,
      regex: /^[A-Z0-9]{4,5}$/,
      transform: (code) => code.toUpperCase(),
    }),
    eccReplyCcEmail: maybeEmailString({ allowDisplayName: true }),
    emailFrom: maybeEmailString({ allowDisplayName: true }),
    emailSignOff: maybeString(),
    hideUnstartedPatrolsBadge: maybeBoolean(),
    name: isString({
      minLength: 3,
      transform: (value) => Case.capital(value),
      trim: 'both',
    }),
    patrollerInstructions: maybeString(),
    patrolMeetUpInstructions: maybeString(),
    patrolNames: asArray(isString({ trim: 'both' }), {
      default: ['Regular Patrol'],
    }),
    policeStation: isString({
      minLength: 3,
      transform: (value) => Case.capital(value),
      trim: 'both',
    }),
    restrictCallSigns: maybeBoolean(),
  };

  export const create = isObject<Create>(createContract);

  export const patchContract: Contract<Patch> = {
    allowQuickPatrol: maybeBoolean(),
    callSigns: maybeArray(
      isString({
        minLength: 2,
        transform: (value) => value.toLocaleUpperCase(),
        trim: 'both',
      }),
    ),
    eccReplyCcEmail: maybeEmailString({ allowDisplayName: true }),
    emailFrom: maybeEmailString({ allowDisplayName: true }),
    emailSignOff: maybeString(),
    hideUnstartedPatrolsBadge: maybeBoolean(),
    name: isString({
      minLength: 3,
      transform: (value) => Case.capital(value),
      trim: 'both',
    }),
    patrollerInstructions: maybeString(),
    patrolMeetUpInstructions: maybeString(),
    patrolNames: maybeArray(isString({ trim: 'both' })),
    policeStation: isString({
      minLength: 3,
      transform: (value) => Case.capital(value),
      trim: 'both',
    }),
    restrictCallSigns: maybeBoolean(),
  };

  export const patch = isObjectSet<Patch>(patchContract);
}
